import { useState, useEffect } from 'react';

interface GeolocationPosition {
  latitude: number;
  longitude: number;
  address: string;
}

interface UseGeolocationReturn {
  position: GeolocationPosition | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
}

// Pas de position par défaut - laissons App.tsx gérer la logique de fallback

export function useGeolocation(): UseGeolocationReturn {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const getLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      // Vérifier si la géolocalisation est disponible
      if (!navigator.geolocation) {
        setPosition(null);
        setError('Géolocalisation non supportée par le navigateur');
        setLoading(false);
        return;
      }

      // Options pour la géolocalisation
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000 // Cache de 5 minutes
      };

      // Obtenir la position
      navigator.geolocation.getCurrentPosition(
        async (geoPosition) => {
          const { latitude, longitude } = geoPosition.coords;

          // Faire du reverse geocoding pour obtenir l'adresse
          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            const address = data.results[0]?.formatted_address || `Position: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

            setPosition({
              latitude,
              longitude,
              address
            });
          } catch (geocodeError) {
            // Si le reverse geocoding échoue, utiliser les coordonnées brutes
            setPosition({
              latitude,
              longitude,
              address: `Position: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
            });
          }
          setLoading(false);
        },
        (geoError) => {
          console.error('Erreur de géolocalisation:', geoError);
          
          // Ne pas essayer la géolocalisation IP ici - App.tsx s'en charge
          setPosition(null);
          setLoading(false);

          // Définir l'erreur pour information
          switch (geoError.code) {
            case geoError.PERMISSION_DENIED:
              setError('Permission refusée pour accéder à votre position');
              break;
            case geoError.POSITION_UNAVAILABLE:
              setError('Position indisponible');
              break;
            case geoError.TIMEOUT:
              setError('Délai dépassé pour obtenir la position');
              break;
            default:
              setError('Erreur inconnue lors de la géolocalisation');
          }
        },
        options
      );
    } catch (err) {
      console.error('Erreur lors de la géolocalisation:', err);
      setPosition(null);
      setError('Erreur lors de la géolocalisation');
      setLoading(false);
    }
  };

  useEffect(() => {
    getLocation();
  }, []);

  return {
    position,
    error,
    loading,
    refresh: getLocation
  };
}